// SPDX-License-Identifier: AGPL-3.0-or-later

import {findUrlEnd, isUrlStart} from '@app/features/messaging/utils/markdown/UrlSpanUtils';

export type ComposerInsertKind = 'keystroke' | 'urlPaste' | 'bulkPaste';

export type ComposerDraftChange =
	| {type: 'insert'; kind: ComposerInsertKind; chars: number}
	| {type: 'delete'; chars: number};

/** Characters kept either side of an edit so an interrupt phrase completed by it still fits the window. */
const CHANGE_WINDOW_PAD = 32;

function draftDiffBounds(previous: string, next: string): {prefix: number; suffix: number} {
	let prefix = 0;
	const maxPrefix = Math.min(previous.length, next.length);
	while (prefix < maxPrefix && previous[prefix] === next[prefix]) {
		prefix += 1;
	}
	let suffix = 0;
	const maxSuffix = Math.min(previous.length - prefix, next.length - prefix);
	while (suffix < maxSuffix && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) {
		suffix += 1;
	}
	return {prefix, suffix};
}

export function sliceComposerDraftDiff(previous: string, next: string): {inserted: string; deleted: string} {
	const {prefix, suffix} = draftDiffBounds(previous, next);
	return {
		inserted: next.slice(prefix, next.length - suffix),
		deleted: previous.slice(prefix, previous.length - suffix),
	};
}

function isWhitespaceAt(text: string, index: number): boolean {
	const character = text[index];
	return character !== undefined && /\s/.test(character);
}

/**
 * Text around the edit, for phrase matching that should not rescan the whole draft on every keystroke.
 * Edges grow out to whitespace so a cut cannot invent a word boundary (matching "shit" inside "shitty").
 */
export function composerDraftChangeWindow(previous: string, next: string, pad = CHANGE_WINDOW_PAD): string {
	const {prefix, suffix} = draftDiffBounds(previous, next);
	let start = Math.max(0, prefix - pad);
	let end = Math.min(next.length, next.length - suffix + pad);
	while (start > 0 && !isWhitespaceAt(next, start - 1)) {
		start -= 1;
	}
	while (end < next.length && !isWhitespaceAt(next, end)) {
		end += 1;
	}
	return next.slice(start, end);
}

export function isEntirelyOneUrl(text: string): boolean {
	const trimmed = text.trim();
	if (trimmed.length === 0 || !isUrlStart(trimmed, 0)) return false;
	return findUrlEnd(trimmed, 0) === trimmed.length;
}

export function classifyComposerDraftChange(previous: string, next: string): ComposerDraftChange | null {
	if (previous === next) return null;
	const {inserted, deleted} = sliceComposerDraftDiff(previous, next);
	if (inserted.length > 0) {
		if (inserted.length === 1) {
			return {type: 'insert', kind: 'keystroke', chars: 1};
		}
		if (isEntirelyOneUrl(inserted)) {
			return {type: 'insert', kind: 'urlPaste', chars: 1};
		}
		return {type: 'insert', kind: 'bulkPaste', chars: inserted.length};
	}
	if (deleted.length > 0) {
		return {type: 'delete', chars: deleted.length};
	}
	return null;
}
